import { Subject } from 'rxjs';

import { CoordinatorService } from './coordinator.service';
import { Project } from './project';
import { ProjectInList } from './project-in-list';
import { Section } from './section';
import { Temporal } from '@js-temporal/polyfill';
import { ProjectTeam } from './project-team';
import { MemberCloudFile } from './member-cloud-file';
import { ScriptInput } from './script-input';

export class Project$ {
    lastIsReady = false;

    private _messageWidget?: any;
    private _isDisplayOn = false;
    isLoading = false;
    cloudProjectsLoading = false;

    sectionMap = new Map<string, Section>();
    isLoadExportedOn = false;

    private _current: Project | undefined;
    private _currentSection!: Section;

    projectLoaded$ = new Subject();
    exportChange$ = new Subject();

    private _invitationsCodes$ = new Subject();
    invitationsCodes$ = this._invitationsCodes$.asObservable();

    lastSaveTime: Temporal.Instant = Temporal.Now.instant();
    pkeySet = new Set<string>();

    private _projectInListMap = new Map<string, ProjectInList>();

    startPILid = '';

    uploadSet = new Set<MemberCloudFile>();

    constructor(private coords: CoordinatorService) {}

    onStart() {
        const sLs: Section[] = [];
        sLs.push(new Section(this.coords, 'general'));
        sLs.push(new Section(this.coords, 'team'));
        this.coords.sectionOrderLs.forEach((x) => sLs.push(new Section(this.coords, x)));
        sLs.push(new Section(this.coords, 'attachments'));
        sLs.push(new Section(this.coords, 'duplkeys'));
        sLs.forEach((x) => this.sectionMap.set(x.tag, x));
        this._currentSection = this.sectionLs[0];
    }

    importViewer() {
        const p = this.current!;
        p.importViewer();
    }

    get isCurrentReady() {
        const isReady = this.coords.dataset$.isReady;

        if (!this.lastIsReady && isReady) {
            this.lastIsReady = true;
            this.coords.display$.markForCheck();
        }
        return isReady;
    }

    async runWhenReady(fun: Function) {
        for (let i = 0; i < 100; i++) {
            if (this.isCurrentReady) return fun();
            await this.coords.utils$.wait(0.1);
        }
    }

    public get isDisplayOn() {
        if (this._isDisplayOn && !this._current) {
            console.log('PROJECT IS-DISPLAY-ON WHILE NOT CURRENT');
            this._isDisplayOn = false;
        }
        return this._isDisplayOn;
    }

    public set isDisplayOn(value) {
        this._isDisplayOn = value;
        if (value) this.coords.project$.isLoading = false;
        this.coords.display$.closeDialog();
        this.coords.display$.markForCheck();
    }

    public get current(): Project | undefined {
        return this._current;
    }

    async setCurrent(value: Project | undefined) {
        this._current = value;
    }

    async runPiam(projectId: string) {
        const resp = await this.coords.firebase$.callFunction('piam', { pid: projectId });
        if (resp === 'ERROR') return false;
        if (!resp) return false;
        await this.coords.updateAuthClaims();
        return true;
    }

    async listenCloudProjects(userId: string) {
        const CLOUD_PROJECTS_LIMIT = 1000;
        this.cloudProjectsLoading = true;
        this.coords.display$.markForCheck();

        const uns = await this.coords.firebase$.firestore.listenQuery(
            'teams',
            { field: 'userId', value: userId },
            async (params) => {
                const hostIdLs: string[] = [];
                const ls = params.docChanges().map(async (itm: any) => {
                    const d = itm.doc.data();
                    const pilId = d.projectId || d.bookId;
                    const oldPil = this.coords.project$.getProjectInList(pilId);

                    switch (itm.type) {
                        case 'added':
                            if (oldPil) return;
                            const pil = new ProjectInList(this.coords, {
                                id: pilId,
                                isCloud: true,
                                isPending: d.isPending,
                                role: d.role,
                                created: d.created,
                            });
                            if (pil.isPending) {
                                pil.setHost(d.hostId, d.created);
                                hostIdLs.push(d.hostId);
                            }
                            await pil.finalizeFromCloud();
                            return;
                        case 'modified':
                            if (oldPil) {
                                if (oldPil.role === 'owner') {
                                    await oldPil.finalizeFromCloud();
                                    return;
                                }
                                oldPil.isPending = d.isPending;
                                oldPil.role = d.role;
                                this.coords.display$.markForCheck();
                            }
                            return;
                        case 'removed':
                            oldPil && this.unsetProjectInList(oldPil);
                            return;
                    }
                });
                this.coords.user$.refreshUserLs(hostIdLs);
                await Promise.all(ls);
                if (this.cloudProjectsLoading) {
                    this.cloudProjectsLoading = false;
                    this.coords.display$.markForCheck();
                }
            },
            CLOUD_PROJECTS_LIMIT,
        );
        uns && this.coords.user$.addQuerySubscription('cloudProjects', uns);
    }

    public get currentSection(): Section {
        return this._currentSection;
    }
    public set currentSection(value: Section) {
        this._currentSection = value;
        this.coords.display$.markForCheck();
    }

    get inCloud() {
        return !!this.current?.isCloud;
    }

    get currentId() {
        return this.current?.id;
    }

    get currentTitle() {
        if (!this.current) return '';
        return this.current.title;
    }

    async clear() {
        this.isDisplayOn = false;
        this.coords.isManager = true;
        this.lastIsReady = false;
        this.cloudProjectsLoading = false;
        await this.current?.terminate();
        this.coords.report$.clear();
        this._currentSection = this.sectionLs[0];
        this.coords.user$.clear();
        this.coords.autouser$.clear();
        this.coords.manifest$.clear();
        this.coords.view$.clear();
        this.coords.filter$.clear();
        this.coords.keyword$.clear();
        this.coords.databin$.clear();
        this.coords.pin$.clear();
        this.coords.highlight$.clear();
        this.coords.binaryTree$.clear();
        this.coords.comment$.clear();
        this.coords.dataset$.clear();
        this.coords.personMap.clear();
        this.coords.script$.clear();
        this.coords.batch$.clear();
        this.coords.searchArchive$.clear();
    }

    get sectionLs() {
        return Array.from(this.sectionMap.values());
    }

    async loadNew() {
        const lastInput = new ScriptInput(this.coords);

        await lastInput.readFile();
        await lastInput.asNew();
    }

    export() {
        this.coords.script$.system$.exportProject();
    }

    get projectList() {
        const ls = Array.from(this._projectInListMap.values()).filter(
            (x) => !x.isPending && x.title,
        );
        try {
            ls.sort((a, b) => Temporal.ZonedDateTime.compare(b.lastOpen!, a.lastOpen!));
        } catch (e) {
            console.log(e);
        }
        return ls;
    }

    get recentProjectList() {
        return this.projectList.slice(0, 5);
    }

    async createProjectBook(params: any) {
        const prBookData = (await this.coords.firebase$.callFunction('createBook', {
            title: params.title,
        })) as any;

        console.log('prBookData', prBookData);

        const book = prBookData.data.book;

        const pil = new ProjectInList(this.coords, {
            id: book.id,
            title: book.title,
            isCloud: true,

            lastUpdate: Temporal.Now.instant().toString(),
        });
        this.setProjectInList(pil);
        pil.select();
        return;
    }

    async finalizeCreateProject() {}

    async toCloud(projectId: string, closeDialog: boolean = true) {
        if (closeDialog) {
            this.coords.project$.isDisplayOn = false;
            this.coords.display$.dialog$.next({
                action: 'message',
                origin: this,
                message: this.coords.text('uploadingLocalDataToCloud'),
            });
        }
        const pil = this.getProjectInList(projectId)!;
        pil.setToCloud();
        this.makeCloud(pil.id);
    }
    async create(params?: {
        inCloud: boolean;
        title: string;
        description?: string;
        autoOpen?: boolean;
    }) {
        const autoOpen = params?.autoOpen !== false; // Default to true if not specified
        if (autoOpen) {
            this.isDisplayOn = false;
        }
        const values: any = { title: params?.title || 'NEW' };
        if (params?.description) {
            values.description = params.description;
        }
        const prData = {
            id: this.coords.uniqueId(),
            type: 'project',

            valueMap: new Map<string, any>(Object.entries(values)),
        };
        this.coords.manifest$.store(prData);

        await this.coords.utils$.wait(0.5);

        const pil = new ProjectInList(this.coords, {
            id: prData.id,
            title: params?.title || 'NEW',

            lastUpdate: Temporal.Now.instant().toString(),
        });
        pil.isFirstRun = true;
        pil.skipAutoOpen = !autoOpen;
        this.setProjectInList(pil);
        if (params?.inCloud) {
            this.toCloud(prData.id, autoOpen);
            return pil;
        } else {
            await pil.localOpenUpdate();
            if (autoOpen) {
                pil.select();
            }
        }
        return pil;
    }

    load3(params: any) {
        try {
            return new Project(this.coords, params);
        } catch (e) {
            this.coords.utils$.consoleError('PROJECT LOAD3 ERROR', e);
            return undefined;
        }
    }

    async makeCloud(projectId: string) {
        await this.coords.project$.setCurrent(undefined);
        this.coords.upload$.localProjectCloudUpload(projectId);
    }

    async loadCloudProject(projectId: string) {
        const pt = new ProjectTeam(this.coords, projectId);
        await pt.init();
    }

    async loadLocalProject(projectId: string) {
        const m$ = this.coords.manifest$;
        m$.pauseStore = true;
        try {
            const rH = await navigator.storage.getDirectory();
            const dH = await rH.getDirectoryHandle(projectId);
            const mp = await this.coords.utils$.filehandleMap(dH);

            const pr = await this.coords.utils$.load3(mp, this.coords.user$.currentId, true);
            if (!pr) return;
            await this.coords.project$.setCurrent(pr);
            this.coords.dataset$.main.requestReduplicate();
            this.coords.display$.markForCheck();

            let pil: ProjectInList | undefined;
            pil = this.coords.project$.getProjectInList(projectId);

            if (!pil && !this.coords.isViewer) {
                pil = new ProjectInList(this.coords, {
                    id: pr.id,
                    title: pr.title,
                    isCloud: false,
                    lastUpdate: Temporal.Now.instant().toString(),
                });
                this.setProjectInList(pil);
            }

            pil && pil.localOpenUpdate();
            pil && pil.scriptLs?.forEach((s) => this.coords.script$.load(s));
        } catch (e) {
            console.error('loadLocalProject', e);
        } finally {
            m$.pauseStore = false;
            this.isDisplayOn = true;
        }
    }

    get title() {
        return this.current?.title;
    }

    getProjectInList(pilId: string) {
        return this._projectInListMap.get(pilId);
    }

    setProjectInList(pil: ProjectInList) {
        this._projectInListMap.set(pil.id, pil);
        this.coords.display$.markForCheck();
        if (this.startPILid && this.startPILid === pil.id) {
            this.startPILid = '';
            if (!pil.skipAutoOpen) {
                pil.select();
            } else {
                console.log('Skipping auto-select due to skipAutoOpen flag');
            }
        }
        this.coords.display$.markForCheck();
    }

    unsetProjectInList(pil: ProjectInList) {
        this._projectInListMap.delete(pil.id);
        this.coords.display$.markForCheck();
    }

    get invitationLs() {
        const ls = Array.from(this._projectInListMap.values()).filter(
            (p) => p.isPending && !p.isLoading && p.role !== 'owner',
        );
        ls.sort((a, b) => b.created - a.created);
        return ls;
    }

    dialogList() {
        this.coords.display$.dialog$.next({
            action: 'project-list',
        });
    }

    async urlPcode(pcode: string) {
        if (!this.coords.user$.current) {
            this.coords.filesystem$.setStartupFile('_pcode', pcode);
            this.coords.user$.loginDialog({
                prefix: this.coords.text('unlogged-code-prefix'),
                suffix: this.coords.text('unlogged-code-suffix'),
            });
            this.coords.user$.startLoginProcess();
            return;
        }

        this.coords.display$.dialog$.next({
            action: 'message',
            message: this.coords.text('processingPCODE'),
        });

        let c = 0;
        while (!this.coords.user$.current && c++ < 10) {
            await this.coords.utils$.wait(c);
        }
        this.coords.user$.current && this.submitInvitationCode(pcode);
    }

    async invitationCodeLs() {
        const resp = await this.coords.firebase$.callFunction('retrieveInvitationCodeList', {
            projectId: this.currentId,
        });
        if (resp === 'ERROR') return [];
        return resp.data as any[];
    }

    async updateInvitationCodes() {
        this._invitationsCodes$.next(await this.invitationCodeLs());
    }

    async submitInvitationCode(code: string) {
        const resp = await this.coords.firebase$.callFunction('acceptInvitationCode', {
            code: code,
        });

        if (resp === 'ERROR') return false;
        const projectId = (resp.data as any).projectId;

        if (projectId) {
            let pil: ProjectInList | undefined;

            for (let i = 0; i < 40; i++) {
                if (pil && pil.title) break;
                await this.coords.utils$.wait(i / 10);
                pil = this.getProjectInList(projectId);
            }
            pil?.select();
        }
        return projectId;
    }

    dialogNewProject() {
        this.coords.display$.dialog$.next({
            action: 'project-new',
        });
    }

    dialogAcceptCode() {
        this.coords.display$.dialog$.next({
            action: 'accept-code',
        });
    }

    localProjectListResponse(ls: any[]) {
        ls.forEach((x: any) => {
            if (!x.title) return;
            const p = new ProjectInList(this.coords, x);
            this.coords.project$.setProjectInList(p);
        });
    }

    deleteAllProjects() {
        this.coords.manifest$.post('delete-all-projects', null);
    }

    openGeneralSection() {
        this.currentSection = this.sectionMap.get('general')!;
    }

    initMessageWidget(messageWidget: any) {
        this._messageWidget = messageWidget;
    }

    destroyMessageWidget() {
        this._messageWidget = undefined;
    }

    refreshMessageWidget(type: string, message: string) {
        this._messageWidget?.refresh(type, message);
    }
}
